// Skeleton primitives — the app-wide cold-load loaders.
//
// Rules (founder ruling 2026-05-29, and the anti-"stuck skeleton" fix):
//   • A skeleton is ALWAYS transient. Render it only while a query is on its
//     COLD load (no cached data yet). Never as a permanent placeholder — that
//     was the bug where dormant/empty screens looked like they were loading.
//   • prefers-reduced-motion: the shimmer animation is disabled via CSS.
//   • Honest-empty and error are SEPARATE states (see Loadable), not skeletons.
//
// Styling lives in src/styles/skeletons.css (imported in main.tsx).

import type { CSSProperties } from "react";

export interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  className?: string;
  style?: CSSProperties;
}

/** Base shimmer block. Compose the variants below from this. */
export function Skeleton({ width, height = 14, radius = 6, className, style }: SkeletonProps): JSX.Element {
  return (
    <span
      className={`skz${className ? ` ${className}` : ""}`}
      aria-hidden="true"
      style={{ width: width ?? "100%", height, borderRadius: radius, ...style }}
    />
  );
}

/** A stack of text lines; last line is shortened like real copy. */
export function SkeletonText({ lines = 3, gap = 8, lastWidth = "62%" }: { lines?: number; gap?: number; lastWidth?: string }): JSX.Element {
  return (
    <span className="skz-text" style={{ display: "flex", flexDirection: "column", gap }} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} height={12} width={i === lines - 1 ? lastWidth : "100%"} />
      ))}
    </span>
  );
}

/** A big-number stat tile loader (matches the scoreboard stat shape). */
export function SkeletonStat(): JSX.Element {
  return (
    <span className="skz-stat" aria-hidden="true">
      <Skeleton width="50%" height={10} radius={4} />
      <Skeleton width="38%" height={30} radius={6} style={{ marginTop: 12 }} />
    </span>
  );
}

/** A generic card body loader. */
export function SkeletonCard({ lines = 3 }: { lines?: number }): JSX.Element {
  return (
    <div className="skz-card" aria-hidden="true">
      <Skeleton width="40%" height={12} radius={4} />
      <Skeleton width="70%" height={20} radius={6} style={{ marginTop: 14 }} />
      <div style={{ marginTop: 16 }}><SkeletonText lines={lines} /></div>
    </div>
  );
}

/** Rows for a table/list loader. `cols` controls cells per row. */
export function SkeletonTableRows({ rows = 5, cols = 4 }: { rows?: number; cols?: number }): JSX.Element {
  return (
    <div className="skz-rows" aria-hidden="true">
      {Array.from({ length: rows }).map((_, r) => (
        <div className="skz-row" key={r}>
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} height={14} width={c === 0 ? "55%" : "80%"} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** A vertical list of card loaders. */
export function SkeletonList({ count = 3, lines = 2 }: { count?: number; lines?: number }): JSX.Element {
  return (
    <div className="skz-list" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div className="skz-list-item" key={i}>
          <SkeletonCard lines={lines} />
        </div>
      ))}
    </div>
  );
}
